/* eslint-disable react/display-name */
import React, { memo } from 'react';
import { VictoryArea, VictoryContainer, VictoryLabelProps, VictoryAxis, VictoryChart } from 'victory';
import styled from 'styled-components';

import { HourlyForecastChartTypes, HourlyForecastYChartKeyTypes, ChartHourlyForecastPeriodCompact } from '../../common/types';
import { PageContentContainerStyle } from '../../styles';
import { ForecastChartNav } from './forecastChartNav';
import { DataLabel } from './dataLabel';

const forecastChartColors = {
    white: '#ffffff'
};

const areaChartStyle = {
    data: {
        fill: forecastChartColors.white, 
        fillOpacity: 0.23, 
        stroke: forecastChartColors.white,
        strokeWidth: 2,
        strokeOpacity: 1
    },
    labels: {
        fontSize: 16,
        fontFamily: 'Source Sans Pro, sans-serif',
        fill: forecastChartColors.white,
        paddingBottom: 13,
        paddingLeft: 0,
        paddingRight: 0
    }
};

const chartAxisStyle = { 
    axis: {
        stroke: 'transparent'
    }, 
    ticks: {
        stroke: 'transparent'
    },
    tickLabels: {
        fill:'transparent'
    } 
};

interface ForecastChartViewProps {
    forecastChartName?: keyof typeof HourlyForecastChartTypes;
    chartWidth: number;
    xAxisLow: number;
    xAxisHigh: number;
    yAxisLow: number;
    yAxisHigh: number;
    chartData: ChartHourlyForecastPeriodCompact[];
}

export const dataLabelFormatter = (
    labelsData: ChartHourlyForecastPeriodCompact[] = [], 
    chartTypeName: keyof typeof HourlyForecastChartTypes, 
    chartLabelIndex: number ): string => {
    const chartLabelIndexInt = Number(chartLabelIndex);

    const labelValue = labelsData?.[chartLabelIndexInt];

    if (chartLabelIndexInt === 0) {
        return '';
    } else if (chartLabelIndexInt === labelsData?.length - 1) {
        return '';
    }
    
    if (chartTypeName && labelValue?.fdtolcl) {
        if (chartTypeName === HourlyForecastChartTypes.precip) {
            return `${labelValue?.originalAdjustedPrecipProbability}%`; // defaulting to original values for labels
        } else if (chartTypeName === HourlyForecastChartTypes.temp) {
            return `${labelValue?.t}°`;
        }
    }
    return '';
};

export const ForecastChartView = memo(({ 
    chartWidth = 2040,
    xAxisLow = 0,
    xAxisHigh = 11,
    yAxisLow = -30,
    yAxisHigh = 100,
    chartData = [],
    forecastChartName = HourlyForecastChartTypes.temp }: ForecastChartViewProps) => {

    const chartYDataType = forecastChartName === HourlyForecastChartTypes.temp 
        ? HourlyForecastYChartKeyTypes.temperature 
        : HourlyForecastYChartKeyTypes.precipitation;

    return (
        <ChartViewContainer>
            <ForecastChartNav />
            <ChartWrapper key='chart-hourly-page-wrapper'>
                <VictoryChart
                    key='v-chart-hourly-view-container'
                    width={chartWidth}
                    height={290}
                    containerComponent={<VictoryContainer style={{ top: 'calc(100% - 290px)' }} responsive={false}/>}
                    domain={{ x: [xAxisLow, xAxisHigh], y: [yAxisLow, yAxisHigh + 5] }}
                    padding={{ right: 0, left: 0, top: 30, bottom: 0 }}>
                    <VictoryArea
                        key='v-area-chart-hourly-view'
                        height={253}
                        width={chartWidth}
                        style={areaChartStyle} 
                        data={chartData}
                        y={chartYDataType}
                        x='plottedPointXPosition'
                        interpolation='natural'
                        labels={(labelData: VictoryLabelProps) => {
                            return dataLabelFormatter(labelData?.data, forecastChartName, labelData?.index);
                        }}
                        labelComponent={<DataLabel dy={-9} />} 
                    />
                    <VictoryAxis
                        key='v-axis-chart-hourly-view'
                        style={chartAxisStyle} 
                    />
                </VictoryChart>
            </ChartWrapper>
            { chartData?.length > 0 // when chart data is available start painting the base.
                ?                     <BaseBackgroundWrapper>
                    <BaseHeight baseChartWidth={chartWidth} />
                </BaseBackgroundWrapper>
                : null
            }
        </ChartViewContainer>
    );
});

const ChartWrapper = styled.div`
    ${PageContentContainerStyle}
    height: 353px;
`;

const BaseBackgroundWrapper = styled.div`
    ${PageContentContainerStyle}
    height: 27px;
`;

interface BaseHeightProp {
    baseChartWidth: number;
}
const BaseHeight = styled.div<BaseHeightProp>`
    display: flex;
    min-width: ${({ baseChartWidth }) =>  baseChartWidth ? `${baseChartWidth}px` : '100%'};
    width: ${({ baseChartWidth }) =>  baseChartWidth ? `${baseChartWidth}px` : '100%'};
    left: 0;
    bottom: 0;
    height: 100%;
    background-color: rgba(255,255,255,0.23);
    content: '';
`;

const ChartViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;